import React, { ForwardRefRenderFunction, forwardRef } from "react";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
//#region types
import { BoardMemberFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { BoardMemberDetailsFormContainerProps } from "models/propTypes/boardMemberPropTypes";
import { WintSuperkollAPIModelExternalCompanyData } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import BoardMemberDetailsForm from "./boardMemberDetailsForm";
import { useCompanyService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { RegularExpressions, UtilsHelper } from "utils/helpers";
import { boardMembersDataUpdated } from "store/actions/boardMembersDataActions";

const BoardMemberDetailsFormContainer: ForwardRefRenderFunction<FormHandles, BoardMemberDetailsFormContainerProps> = (
  props,
  ref
) => {
  const companyService = useCompanyService();
  const { companyId, boardMembers } = props;

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const initialValues: BoardMemberFormTypes.BoardMembers = {
    boardMembers:
      boardMembers?.map((b) => ({
        name: b.name,
        socialSecurityNumber: b.socialSecurityNumber,
        mail: b.mail,
        phoneNumber: b.phoneNumber,
        roles: b.roles?.map((r) => r.roleName).join(", "),
        isTodoCompleted: false,
      })) ?? [],
  };

  const validationSchema = Yup.object<BoardMemberFormTypes.BoardMembers>({
    boardMembers: Yup.array()
      .min(1)
      .of(
        Yup.object<BoardMemberFormTypes.BoardMember>({
          name: Yup.string(),
          mail: Yup.string()
            .email(TranslationKeys.Validations.InvalidEmail)
            .test("preserve-existing-email", TranslationKeys.Validations.Required, function (value) {
              const { socialSecurityNumber } = this.parent;
              // Find the original board member
              const originalBoardMember = boardMembers?.find(
                (original) => original.socialSecurityNumber === socialSecurityNumber
              );
              // If original had an email but current value is empty, fail validation
              if (!UtilsHelper.isEmpty(originalBoardMember?.mail) && UtilsHelper.isEmpty(value)) {
                return false;
              }
              return true;
            }),
          phoneNumber: Yup.string().matches(RegularExpressions.PhoneNumber, {
            message: TranslationKeys.Validations.InvalidPhoneNumber,
            excludeEmptyString: false,
          }),
          socialSecurityNumber: Yup.string(),
          roles: Yup.string(),
        })
      ),
  });

  const onBoardMembersDataUpdated = (isCompleted: boolean) => {
    if (isCompleted) {
      dispatch(boardMembersDataUpdated(0));
    }
  };

  const handleSubmit = (values: BoardMemberFormTypes.BoardMembers) => {
    const updatedBoardMembers: WintSuperkollAPIModelExternalCompanyData.Directors = {
      externalDirectors: values.boardMembers?.map((b) => {
        return {
          name: b.name,
          mail: b.mail,
          phoneNumber: b.phoneNumber,
          socialSecurityNumber: b.socialSecurityNumber,
        };
      }),
    };

    const isCompleted = !values.boardMembers
      ?.filter((b) => (b.socialSecurityNumber ? parseInt(b.socialSecurityNumber[2], 10) < 2 : false))
      .every((b) => !UtilsHelper.isEmpty(b.mail));

    onBoardMembersDataUpdated(isCompleted);

    return companyService.updateCompanyBoardMembersDetails(companyId, updatedBoardMembers);
  };

  return (
    <BoardMemberDetailsForm
      ref={ref}
      {...props}
      defaultValues={initialValues}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
    />
  );
};

export default forwardRef(BoardMemberDetailsFormContainer);
